import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../stores';
import { getLoginPath, isLoginPathname } from '../utils/app-paths';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRole?: 'agent' | 'company-admin' | 'system-admin';
}

/**
 * Componente per proteggere route che richiedono autenticazione.
 *
 * @param children - Contenuto da renderizzare se autenticato
 * @param requiredRole - Ruolo richiesto (opzionale)
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredRole,
}) => {
  const location = useLocation();
  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth
  );

  // Se siamo già sul login, non fare redirect (evita loop)
  // IMPORTANTE: Questo previene redirect infiniti quando handleUnauthorized() fa window.location.replace
  if (isLoginPathname(location.pathname)) {
    return <>{children}</>;
  }

  // Se non autenticato, verifica se handleUnauthorized() sta già gestendo il redirect
  if (!isAuthenticated) {
    // Controlla se c'è un redirect in corso tramite handleUnauthorized()
    const redirectFlag = sessionStorage.getItem('b2b_redirecting_to_login');
    if (redirectFlag) {
      // handleUnauthorized() sta già gestendo il redirect, non fare nulla
      return null;
    }
    
    // Se non c'è redirect in corso, fai redirect normale (fallback)
    // Questo gestisce il caso in cui l'utente naviga direttamente a una route protetta
    return <Navigate to={getLoginPath()} state={{ from: location }} replace />;
  }

  // Se richiesto ruolo specifico, verifica
  if (requiredRole && user) {
    const userRole = user.globalRole || user.b2bRole || user.companyRole;

    // Mapping ruoli
    const hasRequiredRole = checkRole(userRole, requiredRole);

    if (!hasRequiredRole) {
      // Redirect a pagina appropriata in base al ruolo
      return <Navigate to={getLoginPath()} replace />;
    }
  }

  return <>{children}</>;
};

/**
 * Verifica se utente ha il ruolo richiesto.
 */
function checkRole(
  userRole: string | null,
  requiredRole: string
): boolean {
  if (!userRole) return false;

  // system-admin ha accesso a tutto
  if (userRole === 'system-admin') return true;

  // Mapping ruoli
  const roleHierarchy: Record<string, string[]> = {
    'system-admin': ['system-admin', 'company-admin', 'agent'],
    'company-admin': ['company-admin', 'agent'],
    agent: ['agent'],
  };

  const allowedRoles = roleHierarchy[userRole] || [];
  return allowedRoles.includes(requiredRole);
}

export default AuthGuard;
